import React from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { useAtomValue } from "jotai";
import { useClickAway } from "@uidotdev/usehooks";

import { SearchEntitySelector } from "@/components/common/Search/SearchEntitySelector";
import { SearchInput } from "@/components/common/Search/SearchInput";
import { SearchResults } from "@/components/common/Search/SearchResults";
import { SearchResultsSkeleton } from "@/components/common/Search/SearchResultsSkeleton";
import { assetEntity } from "@/components/common/Search/types";
import { IconButton } from "@/components/ui/IconButton";
import { Close20 } from "@/components/ui/SvgIcon";

import { useSearchQuery } from "@/features/trips/queries/useSearchQuery";

import cn from "@/utils/cn";

import { windowHeight } from "@/atoms";

type Props = {
  close: () => void;
};
export const SearchMobile = ({ close }: Props) => {
  const [entity, setEntity] = React.useState(assetEntity);
  const [query, setQuery] = React.useState("");
  const height = useAtomValue(windowHeight);

  const cssVars = {
    "--vh": height + "px",
  } as React.CSSProperties;

  const { data, isFetching, isFetched } = useSearchQuery({
    searchString: query,
    entity,
  });

  const navigate = useNavigate();
  const onItemSelected = (to: string) => {
    close();
    navigate(to);
  };

  const ref = useClickAway<HTMLDivElement>(close);

  return (
    <>
      <div
        data-testid="mobile-search"
        style={cssVars}
        className="fixed inset-0 z-[200] h-screen bg-chorus-black-300/50 md:hidden"
      >
        <div
          ref={ref}
          className="relative mt-[64px] flex h-[calc(var(--vh)-64px)] flex-col rounded-t-[20px] bg-white"
        >
          <Command>
            <div className="relative h-[112px] border-b border-chorus-gray-100 px-4 py-5 before:absolute before:inset-x-0 before:top-full before:z-10 before:mt-px before:h-[32px] before:bg-gradient-to-t before:from-transparent before:to-white before:content-[''] [&>div]:flex-1">
              <span className="relative left-1/2 block h-[6px] w-[72px] -translate-x-1/2 self-center rounded-[16px] bg-[#E2E8F0]"></span>

              <IconButton
                data-testid="close-search-mobile"
                icon={<Close20 className="size-4" />}
                size="xxxs"
                variant="link"
                className="absolute right-[12px] top-[12px] rounded-full bg-[#F1F5F9] "
                onClick={close}
              />
              <div className="relative flex pt-5">
                <SearchInput
                  query={query}
                  setQuery={setQuery}
                  isLoading={isFetching}
                  className={cn("bg-[#F1F5F9]", {
                    "bg-chorus-gray-100":
                      isFetching || (!isFetching && query.length > 0),
                  })}
                />
                <SearchEntitySelector
                  entity={entity}
                  setEntity={setEntity}
                  className={cn("bg-[#F1F5F9]", {
                    "bg-chorus-gray-100":
                      isFetching || (!isFetching && query.length > 0),
                  })}
                ></SearchEntitySelector>
              </div>
            </div>
            <div className="h-[calc(var(--vh)-176px)] flex-1 overflow-y-auto">
              <div className="pt-5">
                {!isFetched && isFetching && <SearchResultsSkeleton />}
                {isFetched && (
                  <SearchResults
                    entity={entity}
                    query={query}
                    searchResponse={data}
                    onItemSelected={onItemSelected}
                  />
                )}
              </div>
            </div>
          </Command>
        </div>
      </div>
    </>
  );
};
